import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import {
  MemberDetailSlide,
  MemberDetailSlideLoader,
} from '@euler/app/tv/components/members/MemberDetailSlide';
import { BlurView } from '@euler/components/BlurView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useSimpleMemberList } from '@euler/functions/useMemberList';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { AnimatePresence, MotiView } from 'moti';
import { Suspense, memo, useEffect, useState } from 'react';
import {
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated';
import { noop } from 'rxjs';

type Props = { targetMemberId?: number };

export const MemberDetailScreen = wrapNavigatorScreen(
  (props: Props) => {
    const navigation = useNavigation();
    const { width } = useWindowDimensions();
    const slideWidth = width * 0.7;
    const slideHeight = slideWidth * (700 / 1108);

    return (
      <AnimatePresence>
        <MotiView
          from={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          css={`
            flex: 1;
            justify-content: center;
          `}
        >
          <BlurView
            blurType="dark"
            blurAmount={5}
            style={StyleSheet.absoluteFill}
            reducedTransparencyFallbackColor="white"
          />
          <TouchableWithoutFeedback
            style={StyleSheet.absoluteFill}
            onPress={() => navigation.goBack()}
          >
            <View style={StyleSheet.absoluteFill} />
          </TouchableWithoutFeedback>
          <Suspense
            fallback={
              <MemberDetailSlideLoader
                width={slideWidth}
                height={slideHeight}
              />
            }
          >
            <Content
              width={width}
              slideWidth={slideWidth}
              slideHeight={slideHeight}
              {...props}
            />
          </Suspense>
        </MotiView>
      </AnimatePresence>
    );
  },
  {
    animationEnabled: false,
    presentation: 'transparentModal',
    headerShown: false,
    cardStyle: {
      backgroundColor: 'transparent',
    },
  },
);

const Content = memo(
  ({
    width,
    slideWidth,
    slideHeight,
    targetMemberId,
  }: Props & {
    width: number;
    slideWidth: number;
    slideHeight: number;
  }) => {
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation();
    const members = useSimpleMemberList() ?? [];

    const initialIndex =
      targetMemberId != null
        ? members.findIndex(x => targetMemberId === x.id)
        : members.length > 0
        ? 0
        : -1;

    const [currentIndex, setCurrentIndex] = useState(0);

    const scrollOffset = useSharedValue(0);
    const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
    const onScroll = useAnimatedScrollHandler({
      onScroll: e => {
        scrollOffset.value = e.contentOffset.x;
      },
      onMomentumEnd(e) {
        runOnJS(setCurrentIndex)(Math.floor(e.contentOffset.x / slideWidth));
      },
    });

    useEffect(() => {
      if (initialIndex > 0) {
        setCurrentIndex(initialIndex);
        scrollViewRef.current?.scrollTo({
          x: slideWidth * initialIndex,
          y: 0,
          animated: false,
        });
      }
    }, [initialIndex, scrollViewRef, slideWidth]);

    if (initialIndex < 0) {
      return (
        <EmptyPlaceholder
          iconSize={rpx(150)}
          error={new Error()}
          text="暂无该员工相关信息"
          buttonText="返回"
          onRetry={() => navigation.goBack()}
          css={`
            flex: 1;
          `}
        />
      );
    }

    return (
      <Animated.ScrollView
        ref={scrollViewRef}
        horizontal
        snapToInterval={slideWidth}
        decelerationRate="normal"
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={1}
        onScroll={onScroll}
        onMomentumScrollEnd={noop}
        contentContainerStyle={{
          justifyContent: 'center',
          alignItems: 'center',
          height: slideHeight,
          paddingHorizontal: (width - slideWidth) / 2,
        }}
        css={`
          flex-grow: 0;
        `}
      >
        <TouchableWithoutFeedback
          style={StyleSheet.absoluteFill}
          onPress={() => navigation.goBack()}
        >
          <View style={StyleSheet.absoluteFill} />
        </TouchableWithoutFeedback>
        {members.map((member, index) => {
          return (
            <MemberDetailSlide
              memberId={member.id}
              key={member.id}
              width={slideWidth}
              height={slideHeight}
              index={index}
              scrollOffset={scrollOffset}
              visible={Math.abs(index - currentIndex) <= 2}
            />
          );
        })}
      </Animated.ScrollView>
    );
  },
);
